import Head from 'next/head';
import { PageProps } from './Page.interfaces';
import { IMeta } from '@/interfaces';
import { useAuthentication, useUser } from '@netfront/gelada-identity-library';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { PrivateHeader, PublicHeader } from '../Header';
import { PrivateFooter, PublicFooter } from '../Footer';
import { useAddCustomEvent, useIsMounted } from '@/hooks';
import { Breadcrumbs } from '@netfront/ui-library';
import { Container } from '../Container';
import Link from 'next/link';
import { IconHome } from '../Icons';
import { ViewportButton } from '../ViewportButton';
import { useGoogleAnalytics } from '@netfront/ekardo-content-library';
import { EAnalyticsEvent } from '@/interfaces/enums';
import { useIsParentCreatedBeforeFirstFeb2026 } from '@/hooks/useIsParentCreatedBeforeFirstFeb2026/useIsParentCreatedBeforeFirstFeb2026';

export const Page = ({
  breadCrumbs,
  customCMSPageEventType,
  children,
  footer,
  hasPrivateLayout = false,
  header,
  isPubliclyAccessible,
  meta,
  shouldExcludeFooter = false,
  title,
}: PageProps) => {
  const { getUser } = useUser();
  const { push } = useRouter();
  const { isParentCreatedBeforeFirstFeb2026, isLoading } = useIsParentCreatedBeforeFirstFeb2026();
  const { seoDescription: seoDescriptionProp, seoTitle: seoTitleProp } = meta ?? ({} as IMeta);

  const seoTitle = `${seoTitleProp ? seoTitleProp : title}`;
  const seoDescription = seoDescriptionProp ? seoDescriptionProp : seoTitleProp ? seoTitleProp : title;

  const { isAuthenticated, refreshToken } = useAuthentication();
  const { isMounted } = useIsMounted();
  const { handleAddCustomEvent } = useAddCustomEvent();
  const { trackEvent } = useGoogleAnalytics();

  useEffect(() => {
    if (isPubliclyAccessible || !hasPrivateLayout) {
      return;
    }

    // Only redirect to login if there's no refresh token either — a present refresh token means
    // a token refresh is in flight (Apollo link or middleware), and the cookie will be applied
    // before the next authenticated request.
    if (!isAuthenticated && !refreshToken) {
      push('/login');
      return;
    }

    // TODO: remove this before pushing changes to production
    // if (isParentCreatedBeforeFirstFeb2026 === true) {
    //   push('/phase-two-trial-ended');
    //   return;
    // }

    // eslint-disable-next-line react-hooks/exhaustive-dep
  }, [isAuthenticated, refreshToken, isParentCreatedBeforeFirstFeb2026]);

  useEffect(() => {
    if (!customCMSPageEventType || !getUser()) return;

    handleAddCustomEvent({
      projectId: String(process.env.REACT_APP_PROJECT_ID),
      type: `Page load: ${customCMSPageEventType}`,
    });
  }, [customCMSPageEventType]);

  return isMounted ? (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <meta content={seoDescription} name="description" />
        <meta content={seoTitle} property="og:title" />
        <meta content={seoDescription} property="og:description" />
        <meta content="/logo-icon.jpg" property="og:image" />
        <link href={`${window.location.origin}${window.location.pathname}`} rel="canonical" />
      </Head>
      <div className="flex flex-col min-height-screen relative">
        {header ? header : hasPrivateLayout && isAuthenticated ? <PrivateHeader /> : <PublicHeader />}

        <ViewportButton
          action={{
            linkButton: { id: 'get-help-now', url: '/get-help-now' },
            text: 'Get help now',
            onClick: () => {
              trackEvent(EAnalyticsEvent.GetHelpButtonClick);
            },
          }}
          hideOnRoutes={['/parent']}
        />

        <div className="flex flex-col flex-1 relative">
          <main className="relative flex flex-col flex-1">
            {breadCrumbs && (
              <div className="py-4">
                <Container additionalClassNames="flex flex-row justify-end">
                  <Breadcrumbs
                    items={[
                      {
                        ...(hasPrivateLayout && isAuthenticated
                          ? {
                              content: (
                                <Link className="w-5" href="/dashboard">
                                  <span className="sr-only">Dashboard</span>
                                  <IconHome />
                                </Link>
                              ),
                              key: 'dashboard',
                            }
                          : {
                              content: (
                                <Link className="w-5" href="/">
                                  <span className="sr-only">Home</span>
                                  <IconHome />
                                </Link>
                              ),
                              key: 'home',
                            }),
                      },
                      ...breadCrumbs,
                    ]}
                  />
                </Container>
              </div>
            )}
            {children}
          </main>
        </div>

        {shouldExcludeFooter ? null : footer ? footer : hasPrivateLayout ? <PrivateFooter /> : <PublicFooter />}
      </div>
    </>
  ) : null;
};
